import { ApolloLink } from '@apollo/client'
import { camelCase } from 'camel-case'

import { BaseError } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import { AlertOptions } from '@/modules/shared/providers/AlertProvider'

/**
 * Apollo Link that intercepts mutation responses and surfaces
 * GraphQL-level errors (response.data.[op].errors) via a dialog.
 *
 * Note: This handles *application-layer* errors returned inside
 * the response body — not network/HTTP errors. For those, pair
 * this with an `onError` link.
 *
 * Usage: place this link before HttpLink in the chain.
 *   from([mutationErrorLink, httpLink])
 */
export default function createMutationErrorLink(alertDialog: (options: AlertOptions) => void) {
  return new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      const isMutation = operation.query.definitions.some(
        (def) => def.kind === 'OperationDefinition' && def.operation === 'mutation'
      )
      if (isMutation) {
        const operationName = camelCase(operation.operationName)
        const errors = response?.data?.[operationName]?.errors || response?.errors || []
        if (errors.length > 0) {
          alertDialog({
            type: 'error',
            message: errors.map((error: BaseError) => error.message + '.').join('<br />'),
          })
        }
      }

      return response
    })
  })
}
